import React, { useRef, useEffect, useMemo } from 'react';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Sort,
  Selection,
} from '@syncfusion/ej2-react-grids';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { useGetEncountersByPatientQuery } from '../../../../../services/EncounterService';
import { EncounterGetByPatientRequest } from '../../../../../services/EncounterService/requests/encounterGetByPatientRequest';
import { useEncounterNavigation } from './hooks';
import { StatusTemplate, formatDate, formatPhysicianName } from './templates';

export interface EncountersForPatientGridProps {
  testId?: string;
}

interface EncounterGridItem {
  id: string;
  visitNumber: string;
  accountNumber: string;
  serviceDate: string;
  status: string;
  physician: string;
  admissionSource: string;
}

/**
 * EncountersForPatientGrid - Displays encounters for a specific patient
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for navigation
 * - Clean component structure
 */
export const EncountersForPatientGrid: React.FC<EncountersForPatientGridProps> = ({
  testId,
}) => {
  const gridRef = useRef<GridComponent>(null);
  const [searchParams] = useSearchParams();
  const { handleRowSelected } = useEncounterNavigation();

  // Get parameters from URL
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const mrn = searchParams.get('mrn') || '';

  // Create request object
  const request: EncounterGetByPatientRequest = {
    company,
    center,
    mrn
  };

  // Fetch encounters for patient
  const {
    data: encounters,
    isLoading,
    error,
  } = useGetEncountersByPatientQuery(request, {
    skip: !company || !center || !mrn,
  });

  // Cleanup grid on unmount
  useEffect(() => {
    return () => {
      if (gridRef.current) {
        try {
          gridRef.current.destroy();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  // Transform data for grid display
  const gridData = useMemo(() => {
    if (!encounters) return [];
    return encounters.map((enc) => ({
      id: enc.id || '',
      visitNumber: enc.caseIdentification?.visitNumber?.toString() || 'N/A',
      accountNumber: enc.caseIdentification?.accountNumber || 'N/A',
      serviceDate: formatDate(enc.encounterInfo?.serviceDate),
      status: enc.status || 'Unknown',
      physician: formatPhysicianName(enc.physician?.firstName, enc.physician?.lastName),
      admissionSource: enc.encounterInfo?.admissionSource || 'N/A',
    }));
  }, [encounters]);

  // Create templates
  const statusTemplate = useMemo(
    () => (props: EncounterGridItem) => <StatusTemplate status={props.status} />,
    []
  );

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 2 }}>Loading encounters...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load encounters. Please try again later.
      </Alert>
    );
  }

  // No patient parameters state
  if (!company || !center || !mrn) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Select a patient to view their encounters.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
          Patient Encounters ({gridData.length})
        </Typography>
      </Box>

      {/* Syncfusion Grid */}
      <GridComponent
        ref={gridRef}
        dataSource={gridData}
        height={300}
        allowSorting={true}
        allowPaging={true}
        pageSettings={{ pageSize: 10 }}
        rowSelected={handleRowSelected}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="visitNumber"
            headerText="Visit Number"
            width="140"
            isPrimaryKey={true}
          />
          <ColumnDirective field="accountNumber" headerText="Account Number" width="140" />
          <ColumnDirective field="serviceDate" headerText="Service Date" width="120" />
          <ColumnDirective
            field="status"
            headerText="Status"
            width="120"
            template={statusTemplate}
          />
          <ColumnDirective field="physician" headerText="Physician" width="180" />
          <ColumnDirective field="admissionSource" headerText="Admission Source" width="140" />
        </ColumnsDirective>
        <Inject services={[Page, Sort, Selection]} />
      </GridComponent>
    </Box>
  );
};

export default EncountersForPatientGrid;
